import {
  fetchGetContext,
  fetchGetContextByStateKey,
  fetchGetProfile,
  fetchGetStateByStateKey,
  fetchPutState,
  GetStateResponse,
} from '@iwoca/lapi-client/edge';
import {
  fetchGetSellerBranding,
  fetchGetSharedFunctionality,
  fetchGetUserExternalNotificationPreferences,
} from '@iwoca/lapi-client/iwocapay';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';

import { AuthContextType } from './lapiHooks';
import { useQueryParam } from '../../hooks/useQueryParam';
import { useSuspenseStateKey } from '../../hooks/useStateKey.hook';

export function useSuspenseGetProfile() {
  const { data, refetch } = useSuspenseQuery({
    queryKey: ['fetchGetProfile'],
    queryFn: () => fetchGetProfile({}),
  });

  return {
    profile: data.data,
    getProfile: refetch,
  };
}

export function useSuspenseGetContext() {
  const impersonationStateKey = useQueryParam('state_key');

  const getContextFunction = impersonationStateKey
    ? () => fetchGetContextByStateKey({ stateKey: impersonationStateKey })
    : () => fetchGetContext({});

  const { data, isError, refetch } = useSuspenseQuery({
    queryKey: ['fetchGetContext'],
    queryFn: async () => {
      const data = await getContextFunction();
      return data;
    },
  });

  const contextData: AuthContextType = {
    stateKey: data?.data?.state_key as AuthContextType['stateKey'],
    // @ts-expect-error LAPI not in sync
    isSeller: data?.data?.is_iwocapay_seller,
    isLoggedIn: !!data && !isError,
    user_signup_finished: data?.data?.user_signup_finished || null,
    isOnSoleTraderPilot: data?.data?.is_iwocapay_st_pilot || false,
    // @ts-expect-error LAPI not in sync
    groups: data?.data.groups || null,
  };

  return {
    context: { ...data?.data, ...contextData },
    getContext: refetch,
  };
}

export function useSuspenseUserGroups() {
  const { context, getContext } = useSuspenseGetContext();

  return {
    groups: context.groups || [],
    isOwner: context?.groups?.includes('customers') || false,
    isStaff: context?.groups?.includes('team') || false,
    isAdmin: context?.groups?.includes('iwocapay_seller_admin'),
    isEditor: context?.groups?.includes('iwocapay_seller_editor'),
    isContributor: context?.groups?.includes('iwocapay_seller_contributor'),
    isViewer: context?.groups?.includes('iwocapay_seller_viewer'),
    fetchSiteContext: getContext,
  };
}

export function useSuspenseGetStateByStateKey() {
  const { stateKey } = useSuspenseStateKey();

  const { data, refetch } = useSuspenseQuery({
    queryKey: ['fetchGetState'],
    queryFn: () => fetchGetStateByStateKey({ stateKey: stateKey! }),
  });

  return {
    state: data?.data,
    refetchState: refetch,
  };
}

export function useSuspenseGetSellerBranding() {
  const { stateKey } = useSuspenseStateKey();

  const { data, refetch } = useSuspenseQuery({
    queryKey: ['fetchGetSellerBranding'],
    queryFn: () => fetchGetSellerBranding({ stateKey: stateKey! }),
  });

  return {
    sellerBranding: data.data,
    refetch,
  };
}

export function useSuspenseGetSharedFunctionality() {
  const { stateKey } = useSuspenseStateKey();
  if (!stateKey) throw Error('User has no state key');

  const { data, refetch } = useSuspenseQuery({
    queryKey: ['fetchGetSharedFunctionality'],
    queryFn: () =>
      fetchGetSharedFunctionality({
        stateKey: stateKey,
      }),
  });

  return {
    functionality: data.data,
    refetch,
  };
}

export function useSuspenseGetUserExternalNotifications() {
  const { data, refetch } = useSuspenseQuery({
    queryKey: ['fetchGetUserExternalNotificationPreferences'],
    queryFn: () => fetchGetUserExternalNotificationPreferences({}),
  });

  // Uncommented for mocks if no preferences setup yet

  // const mockPreferenceResponse = [
  //   {
  //     preference_id: 8888,
  //     description: 'Email notifications about the buyer journey',
  //     name: 'buyer journey',
  //     display_name: 'Buyer Signup',
  //     category_type: 'transactional',
  //     is_subscribed: false,
  //   },
  //   {
  //     preference_id: 9999,
  //     description: 'Email notifications about the buyer journey',
  //     name: 'buyer journey',
  //     display_name: 'Buyer Rejected',
  //     category_type: 'transactional',
  //     is_subscribed: true,
  //   },
  // ];

  return {
    userExternalNotificationPreferences: data.data.notification_preferences,
    getUserExternalNotifications: refetch,
  };
}

export function useSuspensePutState() {
  const { stateKey } = useSuspenseStateKey();
  const { refetchState } = useSuspenseGetStateByStateKey();

  const { mutateAsync: putState, isPending } = useMutation({
    mutationFn: async (state: GetStateResponse | undefined) => {
      if (!stateKey) {
        throw new Error('No stateKey was provided.');
      }
      if (!state) {
        throw new Error('No state was provided.');
      }
      await fetchPutState({ stateKey, body: state });
      await refetchState();
    },
  });
  return { putState, isPending };
}
